import React, { useState, useEffect, useCallback, useRef } from 'react';
import './Onboarding.css';
import FeedCatalog from '../components/FeedCatalog';

const API_URL = process.env.REACT_APP_API_URL || '';

const STEP_LABELS = ['API-ключ OpenAI', 'Номенклатура', 'Анализ GPT'];

function Onboarding({ token, onLogout, onStatusChange }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [apiKeyInput, setApiKeyInput] = useState('');
  const [savingKey, setSavingKey] = useState(false);

  const [matchingModelInfo, setMatchingModelInfo] = useState(null);
  const [matchingModelSaving, setMatchingModelSaving] = useState(false);
  const [matchingModelError, setMatchingModelError] = useState('');

  const [uploadProgress, setUploadProgress] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [catalogMode, setCatalogMode] = useState('excel');

  const [generateTriggered, setGenerateTriggered] = useState(false);

  const authHeaders = { Authorization: `Bearer ${token}` };
  const fileInputRef = useRef(null);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/onboarding/status`, { headers: authHeaders });
      if (res.status === 401) {
        onLogout();
        return null;
      }
      if (res.status === 403) {
        // Не-администратор компании, которая ещё не прошла онбординг — сам он это не завершит
        setError('__not_admin__');
        return null;
      }
      if (!res.ok) throw new Error('Не удалось получить статус онбординга');
      const data = await res.json();
      setStatus(data);
      setError('');
      // Сообщаем наверх (App.jsx) актуальный статус — как только он перестаёт
      // требовать мастер (needs_review/ready), родитель сам перестанет рендерить
      // этот компонент и покажет обычное приложение.
      if (onStatusChange && data.onboarding_status) {
        onStatusChange(data.onboarding_status);
      }
      return data;
      // eslint-disable-next-line
    } catch (e) {
      setError(e.message);
      return null;
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);
  useEffect(() => {
    if (status?.catalog_feed_configured) setCatalogMode('feed');
  }, [status?.catalog_feed_configured]);

  useEffect(() => {
    fetch(`${API_URL}/api/settings/matching-model`, { headers: authHeaders })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => { if (data) setMatchingModelInfo(data); })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleSelectMatchingModel = async (modelId) => {
    setMatchingModelSaving(true);
    setMatchingModelError('');
    try {
      const res = await fetch(`${API_URL}/api/settings/matching-model`, {
        method: 'PUT',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({ model: modelId }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || 'Не удалось сохранить выбор модели');
      }
      setMatchingModelInfo((prev) => ({ ...prev, current: modelId }));
    } catch (e) {
      setMatchingModelError(e.message);
    } finally {
      setMatchingModelSaving(false);
    }
  };

  // Пока идёт генерация — опрашиваем статус
  useEffect(() => {
    if (status?.generation?.status === 'running') {
      const t = setInterval(fetchStatus, 2000);
      return () => clearInterval(t);
    }
  }, [status, fetchStatus]);

  const triggerGeneration = useCallback(async () => {
    setError('');
    setGenerateTriggered(true);
    try {
      const res = await fetch(`${API_URL}/api/onboarding/generate`, {
        method: 'POST',
        headers: authHeaders,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok && res.status !== 409) {
        throw new Error(data.detail || 'Не удалось запустить анализ номенклатуры');
      }
      await fetchStatus();
    } catch (e) {
      setError(e.message);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Автозапуск генерации, как только номенклатура загружена
  useEffect(() => {
    if (
      status &&
      status.onboarding_status === 'needs_nomenclature' &&
      status.nomenclature_count > 0 &&
      !status.generation &&
      !generateTriggered
    ) {
      triggerGeneration();
    }
  }, [status, generateTriggered, triggerGeneration]);

  const handleSaveApiKey = async (e) => {
    e.preventDefault();
    setError('');
    setSavingKey(true);
    try {
      const res = await fetch(`${API_URL}/api/onboarding/api-key`, {
        method: 'POST',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({ api_key: apiKeyInput }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Не удалось сохранить ключ');
      setApiKeyInput('');
      await fetchStatus();
    } catch (e) {
      setError(e.message);
    } finally {
      setSavingKey(false);
    }
  };

  const handleUploadNomenclature = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    setError('');
    setUploadProgress(null);

    const formData = new FormData();
    formData.append('file', file);
    formData.append('regenerate_embeddings', 'false');

    try {
      const response = await fetch(`${API_URL}/api/nomenclature/upload`, {
        method: 'POST',
        headers: authHeaders,
        body: formData,
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.detail || 'Ошибка загрузки файла');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n\n');
        buffer = lines.pop();
        for (const line of lines) {
          const trimmed = line.trim();
          if (!trimmed.startsWith('data: ')) continue;
          const progressData = JSON.parse(trimmed.substring(6).trim());
          setUploadProgress(progressData);
          if (progressData.status === 'error') throw new Error(progressData.message);
        }
      }
      await fetchStatus();
    } catch (e) {
      setError(e.message);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  if (loading) {
    return (
      <div className="onboarding-container">
        <div className="onboarding-card">
          <p>Загрузка...</p>
        </div>
      </div>
    );
  }

  if (error === '__not_admin__') {
    return (
      <div className="onboarding-container">
        <div className="onboarding-card">
          <div className="onboarding-header">
            <h1>⏳ Компания настраивается</h1>
            <p>Администратор ещё не завершил первичную настройку — попросите его закончить онбординг.</p>
            <button type="button" className="onboarding-logout" onClick={onLogout}>Выход</button>
          </div>
        </div>
      </div>
    );
  }

  const gen = status?.generation;
  const genFailed = gen?.status === 'error';

  let stepIndex = 0;
  if (status?.onboarding_status !== 'needs_api_key') stepIndex = 1;
  if (status?.onboarding_status !== 'needs_api_key' && status?.nomenclature_count > 0) stepIndex = 2;

  return (
    <div className="onboarding-container">
      <div className="onboarding-card">
        <div className="onboarding-header">
          <h1>📋 Настройка компании</h1>
          <p>Пара шагов — и система будет готова сопоставлять ваши заказы</p>
          <button type="button" className="onboarding-logout" onClick={onLogout}>Выход</button>
        </div>

        <div className="onboarding-steps">
          {STEP_LABELS.map((label, i) => (
            <div key={label} className={`onboarding-step ${i === stepIndex ? 'active' : ''} ${i < stepIndex ? 'done' : ''}`}>
              <span className="step-num">{i < stepIndex ? '✓' : i + 1}</span>
              <span className="step-label">{label}</span>
            </div>
          ))}
        </div>

        {error && <div className="onboarding-error">{error}</div>}

        {status?.onboarding_status === 'needs_api_key' && (
          <div className="onboarding-step-content">
            <h2>Шаг 1. API-ключ OpenAI</h2>
            <p className="step-hint">
              Ключ нужен, чтобы система могла распознавать заказы клиентов и подбирать соответствия через GPT.
              Получить ключ можно на <a href="https://platform.openai.com/api-keys" target="_blank" rel="noreferrer">platform.openai.com/api-keys</a>.
              Формат ключа: <code>sk-...</code>. Мы проверим его рабочим тестовым запросом перед сохранением.
            </p>
            <p className="step-hint">
              Запросы к GPT оплачиваются напрямую в OpenAI по вашему ключу — отдельно от подписки на ИИ:Матчер.
              По факту у клиента с похожим объёмом: около 0.4 ₽ за одну позицию заказа (например, при
              ~30 000 позиций в месяц выходит порядка 12 000 ₽/мес).
            </p>
            <form onSubmit={handleSaveApiKey} className="onboarding-form">
              <input
                type="text"
                value={apiKeyInput}
                onChange={(e) => setApiKeyInput(e.target.value)}
                placeholder="sk-..."
                required
              />
              <button type="submit" disabled={savingKey}>
                {savingKey ? 'Проверка ключа...' : 'Сохранить и проверить'}
              </button>
            </form>

            {matchingModelInfo && (
              <div className="onboarding-model-choice">
                <h3>Модель GPT для сопоставления</h3>
                <p className="step-hint" style={{ marginBottom: 12 }}>
                  Можно поменять позже в Настройках. По умолчанию — более точная и чуть более дорогая модель.
                </p>
                {matchingModelError && <div className="onboarding-error">{matchingModelError}</div>}
                <div className="onboarding-model-options">
                  {matchingModelInfo.options.map((opt) => {
                    const isCurrent = matchingModelInfo.current === opt.id;
                    return (
                      <div
                        key={opt.id}
                        className={`onboarding-model-card ${isCurrent ? 'active' : ''}`}
                        onClick={() => !matchingModelSaving && !isCurrent && handleSelectMatchingModel(opt.id)}
                      >
                        <div className="onboarding-model-card__head">
                          <strong>{opt.label}</strong>
                          {isCurrent && <span className="onboarding-model-card__badge">выбрана</span>}
                        </div>
                        <div className="onboarding-model-card__price">Вход: {opt.price_input} · Выход: {opt.price_output}</div>
                        <div className="onboarding-model-card__desc">{opt.description}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {status?.onboarding_status === 'needs_nomenclature' && status.nomenclature_count === 0 && (
          <div className="onboarding-step-content">
            <h2>Шаг 2. Номенклатура</h2>
            {status.feed_upload_available && <div className="catalog-source-choice" role="group" aria-label="Способ загрузки товаров">
              <button type="button" aria-pressed={catalogMode === 'excel'} onClick={() => setCatalogMode('excel')}>Excel из 1С</button>
              <button type="button" aria-pressed={catalogMode === 'feed'} onClick={() => setCatalogMode('feed')}>YML-фид сайта</button>
            </div>}
            {catalogMode === 'feed' && status.feed_upload_available ? <FeedCatalog token={token} onImported={fetchStatus} /> : <>
            <p className="step-hint">
              Загрузите Excel-файл с номенклатурой: либо один столбец с названиями товаров,
              либо два столбца — название в первом и артикул во втором.
              Если нужно сгруппировать позиции (группы не должны участвовать в сопоставлении сами по себе) —
              выделите название группы <b>жирным</b> прямо в этой же колонке, остальные строки группы —
              обычным шрифтом. Файл без выделений будет загружен как плоский список.
            </p>
            <p className="step-hint">
              Ведёте номенклатуру по <b>характеристикам</b> в 1С?{' '}
              <a
                href={`${API_URL}/api/nomenclature/template-with-characteristics?token=${encodeURIComponent(token)}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                Скачайте пример файла
              </a>{' '}
              — формат (Номенклатура / Артикул / <b>Характеристика</b>) распознаётся автоматически по заголовкам колонок.
            </p>
            <p className="step-hint">
              Чтобы выгрузить такой файл прямо из 1С, есть готовая обработка —{' '}
              <a href="/1c/ВыгрузкаНоменклатурыССХарактеристиками.epf" download>
                скачать обработку (.epf)
              </a>
              . Работает на 1С:Управление торговлей 11; на 1С:ERP и 1С:Комплексная автоматизация
              должна работать тоже (общий торговый модуль), но отдельно не тестировалась.
              Просто перетащите файл в окно 1С:Предприятие (или откройте через «Файл» → «Открыть»)
              и нажмите кнопку «Выгрузить». Если нужна не вся номенклатура, а только часть —
              перед этим укажите «Группу номенклатуры» и/или «Вид номенклатуры».
            </p>
            <label className="onboarding-file-btn">
              {uploading ? 'Загрузка...' : 'Выбрать файл номенклатуры'}
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls"
                onChange={handleUploadNomenclature}
                disabled={uploading}
                hidden
              />
            </label>
            <p className="step-hint">
              Загрузка большого файла (характеристики, embeddings) обычно занимает около 5 минут —
              дождитесь окончания, не закрывайте вкладку.
            </p>
            {uploadProgress && (
              <div className="onboarding-progress">
                {uploadProgress.message || `${uploadProgress.current || 0} / ${uploadProgress.total || '?'}`}
              </div>
            )}
            </>}
          </div>
        )}

        {status?.onboarding_status === 'needs_nomenclature' && status.nomenclature_count > 0 && (
          <div className="onboarding-step-content">
            <h2>Шаг 3. GPT анализирует номенклатуру</h2>
            <p className="step-hint">
              Загружено позиций: {status.nomenclature_count}. Сейчас GPT подберёт категории товаров,
              критичные характеристики и промпты для сопоставления — обычно это занимает несколько минут.
            </p>
            {genFailed ? (
              <>
                <div className="onboarding-error">Ошибка анализа: {gen.message}</div>
                <button type="button" onClick={() => { setGenerateTriggered(false); triggerGeneration(); }}>
                  Повторить
                </button>
              </>
            ) : (
              <div className="onboarding-spinner">
                <div className="spinner" />
                <span>{gen?.message || 'Запуск анализа...'}</span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default Onboarding;
